import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Form, Input, Button, Row, Col, Popconfirm, Card, message } from 'antd';
import { PlusOutlined, MinusCircleOutlined, HolderOutlined } from '@ant-design/icons';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';
import InputWithEmoji from '../../CustomUtils.js/InputWithKeyword';
import ListItemPreview from './ListItemPreview';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Draggable Row Component
const DraggableRow = ({ row, uniqueId, index, handleRowIdChange, handleRowLabelChange, removeRow, rowsLength }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: uniqueId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: '10px',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Row gutter={ROW_GUTTER} align="middle">
        <Col xs={2} sm={1} md={1} className='d-flex justify-content-center align-items-center'>
          <HolderOutlined
            {...listeners}
            style={{ fontSize: '16px', color: '#999', cursor: 'grab' }}
          />
        </Col>
        <Col xs={22} sm={10} md={10}>
          <Form.Item
            label="ID"
            rules={[
              { required: true, message: 'ID is required!' },
              { max: 200, message: 'ID must be at most 200 characters.' },
            ]}
          >
            <Input
              value={row.id}
              showCount
              maxLength={200}
              onChange={(e) => handleRowIdChange(index, e)}
              placeholder="Enter ID"
            />
          </Form.Item>
        </Col>
        <Col xs={22} sm={10} md={10}>
          <Form.Item
            label="Label"
            rules={[
              { required: true, message: 'Label is required!' },
              { max: 24, message: 'Label must be at most 24 characters.' },
            ]}
          >
            <Input
              value={row.label}
              showCount
              onChange={(e) => handleRowLabelChange(index, e)}
              placeholder="Enter Label"
              maxLength={24}
            />
          </Form.Item>
        </Col>
        {rowsLength > 1 && (
          <Col xs={2} sm={2} md={2} className='d-flex justify-content-center align-items-center'>
            <Popconfirm
              title="Are you sure to delete this row?"
              className='cursor-pointer'
              onConfirm={() => removeRow(index)}
              okText="Yes"
              cancelText="No"
            >
              <MinusCircleOutlined style={{ fontSize: '20px', color: 'red' }} />
            </Popconfirm>
          </Col>
        )}
      </Row>
    </div>
  );
};

const ListMessagePreset = () => {
  const { api, authToken, UserData } = useMyContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { editState, presetData } = location.state || {};

  const [header, setHeader] = useState('');
  const [body, setBody] = useState('');
  const [footer, setFooter] = useState('');
  const [name, setName] = useState('');
  const [rows, setRows] = useState([{ id: '', label: '', uniqueId: `row-${Date.now()}-0` }]);
  const [buttonText, setButtonText] = useState('');
  const containerRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    if (editState && presetData) {
      setName(presetData?.name);
      setHeader(presetData?.header);
      setBody(presetData?.body);
      setFooter(presetData?.footer);
      setButtonText(presetData?.button_text);
      const parsedRows = JSON.parse(presetData?.rows) || [{ id: '', label: '' }];
      // Add uniqueId to existing rows
      const rowsWithIds = parsedRows.map((row, index) => ({
        ...row,
        uniqueId: row.uniqueId || `row-${Date.now()}-${index}`
      }));
      setRows(rowsWithIds);

      // Set form values
      form.setFieldsValue({
        Preset_Name: presetData?.name,
        header: presetData?.header,
        footer: presetData?.footer,
        Button_Text: presetData?.button_text,
      });
    }
  }, [editState, presetData, form]);

  const addRow = () => {
    if (rows.length < 10) {
      setRows([...rows, { id: '', label: '', uniqueId: `row-${Date.now()}-${rows.length}` }]);
      // Scroll to bottom after adding a row
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      }, 0);
    } else {
      message.error('Maximum of 10 rows allowed.');
    }
  };

  const removeRow = (index) => {
    const newRows = [...rows];
    newRows.splice(index, 1);
    setRows(newRows);
  };

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setRows((items) => {
      const oldIndex = items.findIndex(item => item.uniqueId === active.id);
      const newIndex = items.findIndex(item => item.uniqueId === over.id);

      return arrayMove(items, oldIndex, newIndex);
    });
  }, []);

  const handleHeaderChange = (e) => {
    const value = e.target.value;
    setHeader(value);
    form.setFieldValue('header', value);
  };

  const handleFooterChange = (e) => {
    const value = e.target.value;
    setFooter(value);
    form.setFieldValue('footer', value);
  };

  const handleRowIdChange = (index, e) => {
    const newRows = [...rows];
    newRows[index].id = e.target.value;
    setRows(newRows);
  };

  const handleRowLabelChange = (index, e) => {
    const newRows = [...rows];
    newRows[index].label = e.target.value;
    setRows(newRows);
  };

  const handleSubmit = async (values) => {
    // Validate body
    if (!body?.trim()) {
      message.error('Body is required!');
      return;
    }

    if (body.length > 1024) {
      message.error('Body must be at most 1024 characters!');
      return;
    }

    // Validate button text
    if (!buttonText?.trim()) {
      message.error('Button text is required!');
      return;
    }

    // Validate rows
    const filteredRows = rows.filter((row) => row.id.trim() !== '' || row.label.trim() !== '');

    if (filteredRows?.length === 0) {
      message.error('At least one row is required.');
      return;
    }

    // Validate each row
    for (let i = 0; i < filteredRows.length; i++) {
      const row = filteredRows[i];

      if (!row.id.trim()) {
        message.error(`Row ${i + 1}: ID is required!`);
        return;
      }

      if (row.id.length > 200) {
        message.error(`Row ${i + 1}: ID must be at most 200 characters!`);
        return;
      }

      // if (/\s/.test(row.id)) {
      //   message.error(`Row ${i + 1}: ID cannot contain spaces!`);
      //   return;
      // }

      if (!row.label.trim()) {
        message.error(`Row ${i + 1}: Label is required!`);
        return;
      }

      if (row.label.length > 24) {
        message.error(`Row ${i + 1}: Label must be at most 24 characters!`);
        return;
      }
    }

    // Remove uniqueId before sending to API
    const rowsForAPI = filteredRows.map(({ uniqueId, ...rest }) => rest);

    const object = {
      messaging_product: "whatsapp",
      type: "interactive",
      to: '#number',
      interactive: {
        type: "list",
        header: {
          type: "text",
          text: header,
        },
        body: {
          text: body,
        },
        footer: {
          text: footer,
        },
        action: {
          button: buttonText,
          sections: [
            {
              title: "Select Option",
              rows: rowsForAPI.map((row, index) => ({
                id: row.id,
                title: row.label,
              })),
            },
          ],
        },
      },
    };

    const payload = {
      user_id: UserData?.id,
      name: name,
      header,
      body,
      footer,
      rows: rowsForAPI,
      button_text: buttonText,
      object,
    }
    try {
      const url = editState
        ? `/list-message-preset/${presetData.id}`
        : `/list-message-preset`;

      const method = editState ? 'put' : 'post';

      const response = await apiClient[method](url, payload);

      message.success(
        editState ? 'Preset updated successfully!' : 'Preset added successfully!'
      );
      console.log(`${editState ? 'Update' : 'Store'} Response:`, response.data);

      navigate(-1);
    } catch (error) {
      console.error('Error submitting preset:', error);
      message.error('Failed to save preset. Please try again.');
    }
  };

  return (
    <Row gutter={ROW_GUTTER}>
      <Col xs={24} sm={24} md={24} lg={18}>
        <Card title="List Message Preset">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            {/* Header and Footer on the same line */}
            <Row gutter={ROW_GUTTER}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  label="Preset Name"
                  name="Preset_Name"
                  rules={[
                    //{ required: true, message: 'Preset Name is required!' },
                  ]}
                >
                  <Input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      form.setFieldValue('Preset_Name', e.target.value);
                    }}
                    placeholder="Enter preset name"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  label="Header"
                  name="header"
                  rules={[
                    { max: 60, message: 'Header must be at most 60 characters.' },
                  ]}
                >
                  <Input
                    value={header}
                    maxLength={60}
                    showCount
                    onChange={handleHeaderChange}
                    placeholder="Enter header text"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  label="Footer"
                  name="footer"
                  rules={[
                    { max: 60, message: 'Footer must be at most 60 characters.' },
                  ]}
                >
                  <Input
                    value={footer}
                    maxLength={60}
                    showCount
                    onChange={handleFooterChange}
                    placeholder="Enter footer text"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={6}>
                <Form.Item
                  label="Button Text"
                  name="Button_Text"
                  rules={[
                    { required: true, message: 'Button text is required!' },
                    { max: 20, message: 'Button text must be at most 20 characters.' },
                  ]}
                >
                  <Input
                    value={buttonText}
                    onChange={(e) => {
                      setButtonText(e.target.value);
                      form.setFieldValue('Button_Text', e.target.value);
                    }}
                    placeholder="Enter Button Text"
                    maxLength={20}
                    showCount
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Body with TextArea */}
            <Row gutter={ROW_GUTTER}>
              <Col xs={24} sm={24} md={24}>
                <InputWithEmoji
                  value={body}
                  hideVarBTN={true}
                  language={'english'}
                  setValue={setBody}
                  limit={1024}
                />
              </Col>
            </Row>

            {/* Dynamic Rows */}
            <label>Rows:</label>
            <div ref={containerRef} style={{ marginBottom: '20px', maxHeight: 300, overflowX: 'hidden', overflowY: 'auto' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={rows.map((row) => row.uniqueId)}
                  strategy={verticalListSortingStrategy}
                >
                  {rows?.map((row, index) => (
                    <DraggableRow
                      key={row.uniqueId}
                      uniqueId={row.uniqueId}
                      index={index}
                      row={row}
                      handleRowIdChange={handleRowIdChange}
                      handleRowLabelChange={handleRowLabelChange}
                      removeRow={removeRow}
                      rowsLength={rows.length}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addRow}
              className='mb-2'
              block
              style={{ marginTop: '10px' }}
            >
              Add Row
            </Button>

            {/* Submit Button */}
            <Row gutter={ROW_GUTTER}>
              <Col xs={24} sm={24} md={24} className='d-flex justify-content-end'>
                <Button type="default" className='mr-2' onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" className='position-relative float-right'>
                  Submit
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>
      </Col>
      <Col xs={24} sm={24} md={24} lg={6}>
        <ListItemPreview
          headerType={'text'}
          header={header}
          body={body}
          footer={footer}
          media={null}
          rows={rows}
          buttonText={buttonText}
        />
      </Col>
    </Row>
  );
};

export default ListMessagePreset;